// ActionExecutor - 統一執行效果動作
// 將 ImmediateAction 轉換成實際的 GameState 變更
import type {
  GameState,
  Card,
  CardDefinition,
  BattlefieldPosition,
} from '../../types';
import type { ImmediateAction, TargetSelector } from './EffectTriggerSystem';
import { getLogger } from '../../utils/logger';
import { hasDestroyResistance, resistsUnitEffect } from './handlers/CombatHandlers';

const log = getLogger('ActionExecutor');

/**
 * 執行結果
 */
export interface ExecutionResult {
  state: GameState;
  success: boolean;
  messages: string[];
  affectedCardIds: string[];
}

/**
 * 動作執行上下文
 */
export interface ExecutionContext {
  sourceCardId: string;
  sourcePosition?: BattlefieldPosition;
  definitions: Map<string, CardDefinition>;
}

/**
 * 統一動作執行器
 */
export class ActionExecutor {
  private definitions: Map<string, CardDefinition>;

  constructor(definitions: Map<string, CardDefinition>) {
    this.definitions = definitions;
  }

  /**
   * 執行單一動作
   */
  execute(
    state: GameState,
    action: ImmediateAction,
    context?: Partial<ExecutionContext>
  ): ExecutionResult {
    const ctx: ExecutionContext = {
      sourceCardId: action.sourceCardId || context?.sourceCardId || '',
      sourcePosition: context?.sourcePosition,
      definitions: this.definitions,
    };

    switch (action.type) {
      case 'draw':
        return this.executeDraw(state, action, ctx);
      case 'discard':
        return this.executeDiscard(state, action, ctx);
      case 'summon':
        return this.executeSummon(state, action, ctx);
      case 'destroy':
        return this.executeDestroy(state, action, ctx);
      case 'move':
        return this.executeMove(state, action, ctx);
      case 'buff':
        return this.executeBuff(state, action, ctx);
      case 'debuff':
        return this.executeDebuff(state, action, ctx);
      case 'custom':
        return this.executeCustom(state, action, ctx);
      default:
        log.warn(`Unknown action type: ${String(action.type)}`);
        return { state, success: false, messages: [], affectedCardIds: [] };
    }
  }

  /**
   * 執行多個動作
   */
  executeAll(
    state: GameState,
    actions: ImmediateAction[],
    context?: Partial<ExecutionContext>
  ): ExecutionResult {
    let currentState = state;
    const allMessages: string[] = [];
    const allAffectedIds: string[] = [];
    let allSuccess = true;

    for (const action of actions) {
      const result = this.execute(currentState, action, context);
      currentState = result.state;
      allMessages.push(...result.messages);
      allAffectedIds.push(...result.affectedCardIds);
      if (!result.success) allSuccess = false;
    }

    return {
      state: currentState,
      success: allSuccess,
      messages: allMessages,
      affectedCardIds: [...new Set(allAffectedIds)],
    };
  }

  // ============================================================
  // 抽牌 / 棄牌
  // ============================================================

  private executeDraw(
    state: GameState,
    action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    const count = typeof action.value === 'number' ? action.value : 1;
    const targetSide = this.resolveTargetSide(state, action.targetSelector?.side);
    const isPlayer1 = targetSide === 'player1';
    
    const player = isPlayer1 ? state.player1 : state.player2;
    const newDeck = [...player.deck];
    const newHand = [...player.hand];
    const actualDraw = Math.min(count, newDeck.length);
    const drawnIds: string[] = [];

    for (let i = 0; i < actualDraw; i++) {
      const card = newDeck.shift();
      if (card) {
        const handCard = { ...card, location: 'hand' as const };
        newHand.push(handCard);
        drawnIds.push(card.id);
      }
    }

    const newState = isPlayer1
      ? { ...state, player1: { ...state.player1, deck: newDeck, hand: newHand } }
      : { ...state, player2: { ...state.player2, deck: newDeck, hand: newHand } };

    return {
      state: newState,
      success: true,
      messages: [`抽了 ${String(actualDraw)} 張牌`],
      affectedCardIds: drawnIds,
    };
  }

  private executeDiscard(
    state: GameState,
    action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    const count = typeof action.value === 'number' ? action.value : 1;
    const targetSide = this.resolveTargetSide(state, action.targetSelector?.side);
    const isPlayer1 = targetSide === 'player1';
    
    const player = isPlayer1 ? state.player1 : state.player2;
    const newHand = [...player.hand];
    const newGraveyard = [...player.graveyard];
    const discardedIds: string[] = [];

    // 隨機棄牌（可擴展為指定棄牌）
    const actualDiscard = Math.min(count, newHand.length);
    for (let i = 0; i < actualDiscard; i++) {
      const randomIdx = Math.floor(Math.random() * newHand.length);
      const card = newHand.splice(randomIdx, 1)[0];
      newGraveyard.push({ ...card, location: 'graveyard' as const });
      discardedIds.push(card.id);
    }

    const newState = isPlayer1
      ? { ...state, player1: { ...state.player1, hand: newHand, graveyard: newGraveyard } }
      : { ...state, player2: { ...state.player2, hand: newHand, graveyard: newGraveyard } };

    return {
      state: newState,
      success: true,
      messages: [`棄掉 ${String(actualDiscard)} 張牌`],
      affectedCardIds: discardedIds,
    };
  }

  // ============================================================
  // 召喚
  // ============================================================

  private executeSummon(
    state: GameState,
    action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    const selector = action.targetSelector;
    if (!selector) {
      return { state, success: false, messages: ['召喚失敗：無目標選擇器'], affectedCardIds: [] };
    }

    const targetSide = this.resolveTargetSide(state, selector.side);
    const isPlayer1 = targetSide === 'player1';
    const count = selector.count ?? 1;
    const cardName = selector.filter?.name;
    
    let newState = state;
    const summonedIds: string[] = [];

    // 根據來源區域獲取卡牌
    let sourceCards: Card[] = [];
    const player = isPlayer1 ? state.player1 : state.player2;

    switch (selector.zone) {
      case 'extra':
        sourceCards = player.extra ?? [];
        break;
      case 'graveyard':
        sourceCards = player.graveyard;
        break;
      case 'deck':
        sourceCards = player.deck;
        break;
      case 'hand':
        sourceCards = player.hand;
        break;
    }

    // 過濾符合條件的卡牌
    let candidates = sourceCards;
    if (cardName) {
      candidates = candidates.filter(card => {
        const def = this.definitions.get(card.definitionId);
        return def?.name === cardName || def?.nameZh === cardName || card.definitionId.includes(cardName);
      });
    }

    // 尋找空位
    const emptySlots = this.findEmptySlots(newState, targetSide);
    const toSummon = Math.min(count, candidates.length, emptySlots.length);

    for (let i = 0; i < toSummon; i++) {
      const card = candidates[i];
      const slot = emptySlots[i];
      
      // 創建召喚的單位
      const summonedCard: Card = {
        ...card,
        id: `summoned-${card.definitionId}-${String(Date.now())}-${String(i)}`,
        location: 'battlefield',
        position: {
          side: targetSide,
          row: slot.row,
          column: slot.column as 1 | 2 | 3 | 4 | 5,
        },
        orientation: 'upright',
        summonedThisTurn: true,
        ownerId: isPlayer1 ? state.player1.id : state.player2.id,
      };

      // 放置到戰場
      newState = this.placeOnBattlefield(newState, summonedCard, slot);
      
      // 從來源區域移除（如果是從墓地/牌庫召喚）
      if (selector.zone === 'graveyard') {
        newState = this.removeFromZone(newState, targetSide, 'graveyard', card.id);
      } else if (selector.zone === 'deck') {
        newState = this.removeFromZone(newState, targetSide, 'deck', card.id);
      }

      summonedIds.push(summonedCard.id);
    }

    return {
      state: newState,
      success: toSummon > 0,
      messages: [`召喚了 ${String(toSummon)} 個單位`],
      affectedCardIds: summonedIds,
    };
  }

  // ============================================================
  // 破壞
  // ============================================================

  private executeDestroy(
    state: GameState,
    action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    const selector = action.targetSelector;
    if (!selector) {
      return { state, success: false, messages: ['破壞失敗：無目標選擇器'], affectedCardIds: [] };
    }

    const targetSide = this.resolveTargetSide(state, selector.side);
    const count = selector.count ?? 1;

    let newState = state;
    const destroyedIds: string[] = [];

    // 獲取目標單位
    const units = this.getUnitsOnBattlefield(state, targetSide, selector.filter);
    const toDestroy = selector.random 
      ? this.selectRandom(units, count)
      : units.slice(0, count);

    for (const { card, position } of toDestroy) {
      // Destroy resistance check
      if (hasDestroyResistance(card.keywords)) {
        log.debug(`${card.id} 破壞抗性，無法被破壞`);
        continue;
      }

      // Effect resistance check (when source is a unit on battlefield)
      if (_ctx.sourceCardId) {
        const sourceCard = this.findCardOnBattlefield(state, _ctx.sourceCardId);
        if (sourceCard && card.ownerId) {
          if (resistsUnitEffect(card.keywords, card.ownerId, sourceCard.card.ownerId)) {
            log.debug(`${card.id} 效果抗性，無法被效果破壞`);
            continue;
          }
        }
      }

      // 從戰場移除
      newState = this.removeFromBattlefield(newState, position);
      
      // 加入墓地
      const playerKey = card.ownerId === state.player1.id ? 'player1' : 'player2';
      const graveCard: Card = { ...card, location: 'graveyard', position: undefined };
      newState = {
        ...newState,
        [playerKey]: {
          ...newState[playerKey],
          graveyard: [...newState[playerKey].graveyard, graveCard],
        },
      };

      destroyedIds.push(card.id);
    }

    return {
      state: newState,
      success: destroyedIds.length > 0,
      messages: [`破壞了 ${String(destroyedIds.length)} 個單位`],
      affectedCardIds: destroyedIds,
    };
  }

  // ============================================================
  // 移動
  // ============================================================

  private executeMove(
    state: GameState,
    _action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    // TODO: 實作移動邏輯
    return {
      state,
      success: true,
      messages: ['移動效果觸發'],
      affectedCardIds: [],
    };
  }

  // ============================================================
  // Buff / Debuff
  // ============================================================

  private executeBuff(
    state: GameState,
    action: ImmediateAction,
    ctx: ExecutionContext
  ): ExecutionResult {
    return this.applyAtkModifier(state, action, ctx, true);
  }

  private executeDebuff(
    state: GameState,
    action: ImmediateAction,
    ctx: ExecutionContext
  ): ExecutionResult {
    return this.applyAtkModifier(state, action, ctx, false);
  }

  private applyAtkModifier(
    state: GameState,
    action: ImmediateAction,
    ctx: ExecutionContext,
    isBuff: boolean
  ): ExecutionResult {
    const rawValue = typeof action.value === 'number' ? action.value : 0;
    const modifier = isBuff ? Math.abs(rawValue) : -Math.abs(rawValue);
    
    // 如果是 selfBuff，只作用於來源卡
    if (action.customHandler === 'selfBuff' && ctx.sourcePosition) {
      let newState = state;
      const affected = this.modifyUnitAtk(newState, ctx.sourcePosition, modifier);
      if (affected) {
        newState = affected.state;
        return {
          state: newState,
          success: true,
          messages: [`${isBuff ? '強化' : '弱化'} ${String(modifier)} 攻擊力`],
          affectedCardIds: [ctx.sourceCardId],
        };
      }
    }

    // 否則根據 targetSelector 選擇目標
    const selector = action.targetSelector;
    const targetSide = this.resolveTargetSide(state, selector?.side);
    const units = this.getUnitsOnBattlefield(state, targetSide, selector?.filter);
    
    let newState = state;
    const affectedIds: string[] = [];

    for (const { card, position } of units) {
      const result = this.modifyUnitAtk(newState, position, modifier);
      if (result) {
        newState = result.state;
        affectedIds.push(card.id);
      }
    }

    return {
      state: newState,
      success: affectedIds.length > 0,
      messages: [`${String(affectedIds.length)} 個單位 ${isBuff ? '+' : ''}${String(modifier)} 攻擊力`],
      affectedCardIds: affectedIds,
    };
  }

  // ============================================================
  // Custom Handler
  // ============================================================

  private executeCustom(
    state: GameState,
    action: ImmediateAction,
    ctx: ExecutionContext
  ): ExecutionResult {
    const handler = action.customHandler;
    
    switch (handler) {
      case 'reward':
      case 'addReward':  // V2 handler 使用 addReward
        return this.executeReward(state, action, ctx);
      case 'subtractReward':
        return this.executeSubtractReward(state, action, ctx);
      case 'charge':
        return this.executeCharge(state, action, ctx);
      case 'poison':
        return this.executePoison(state, action, ctx);
      case 'onSneakSuccess':
        return this.executeSneakSuccess(state, action, ctx);
      case 'onCombatSuccess':
        return this.executeCombatSuccess(state, action, ctx);
      default:
        log.debug(`Custom handler: ${handler ?? 'unknown'}`);
        return { state, success: true, messages: [`觸發 ${handler ?? 'unknown'} 效果`], affectedCardIds: [] };
    }
  }

  private executeReward(
    state: GameState,
    action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    const value = typeof action.value === 'number' ? action.value : 1;
    const targetSide = this.resolveTargetSide(state, action.targetSelector?.side);
    const isPlayer1 = targetSide === 'player1';
    
    // 更新玩家獎勵值
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const currentReward = state[playerKey].reward ?? 0;
    const newReward = currentReward + value;

    const newState = {
      ...state,
      [playerKey]: {
        ...state[playerKey],
        reward: newReward,
      },
    };

    return {
      state: newState,
      success: true,
      messages: [`${targetSide === 'player1' ? '玩家1' : '玩家2'} 獎勵 +${String(value)}`],
      affectedCardIds: [],
    };
  }

  private executeSubtractReward(
    state: GameState,
    action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    const value = typeof action.value === 'number' ? action.value : 1;
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    
    const currentReward = state[playerKey].reward ?? 0;
    const newReward = Math.max(0, currentReward - value);

    const newState = {
      ...state,
      [playerKey]: {
        ...state[playerKey],
        reward: newReward,
      },
    };

    return {
      state: newState,
      success: true,
      messages: [`獎勵 -${String(value)}`],
      affectedCardIds: [],
    };
  }

  private executeCharge(
    state: GameState,
    action: ImmediateAction,
    ctx: ExecutionContext
  ): ExecutionResult {
    const value = typeof action.value === 'number' ? action.value : 1;
    
    // 給來源卡增加充能
    if (!ctx.sourcePosition) {
      return { state, success: false, messages: ['充能失敗：無來源位置'], affectedCardIds: [] };
    }

    const slot = this.getSlot(state, ctx.sourcePosition);
    if (!slot.unit) {
      return { state, success: false, messages: ['充能失敗：無單位'], affectedCardIds: [] };
    }

    const currentCharge = slot.unit.charge ?? 0;
    const newCharge = currentCharge + value;

    const newState = this.updateUnitProperty(state, ctx.sourcePosition, { charge: newCharge });

    return {
      state: newState,
      success: true,
      messages: [`充能 +${String(value)} (當前: ${String(newCharge)})`],
      affectedCardIds: [slot.unit.id],
    };
  }

  private executePoison(
    state: GameState,
    _action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    // 劇毒效果：戰鬥後將目標送入森林而非墓地
    // 這個效果會在 CombatSystem 中根據狀態判斷
    return {
      state,
      success: true,
      messages: ['劇毒效果已標記'],
      affectedCardIds: [],
    };
  }

  private executeSneakSuccess(
    state: GameState,
    action: ImmediateAction,
    ctx: ExecutionContext
  ): ExecutionResult {
    // 偷襲成功時的效果
    if (action.value === 'summonFromGraveyard') {
      // 從墓地召喚
      const summonAction: ImmediateAction = {
        type: 'summon',
        sourceCardId: ctx.sourceCardId,
        targetSelector: action.targetSelector,
      };
      return this.executeSummon(state, summonAction, ctx);
    }

    return { state, success: true, messages: ['偷襲成功'], affectedCardIds: [] };
  }

  private executeCombatSuccess(
    state: GameState,
    _action: ImmediateAction,
    _ctx: ExecutionContext
  ): ExecutionResult {
    // 戰鬥成功時的通用處理
    return { state, success: true, messages: ['戰鬥成功'], affectedCardIds: [] };
  }

  // ============================================================
  // 輔助方法
  // ============================================================

  private resolveTargetSide(
    state: GameState,
    side?: 'self' | 'enemy' | 'both' | 'any'
  ): 'player1' | 'player2' {
    const activeIsPlayer1 = state.activePlayerId === state.player1.id;
    
    if (side === 'enemy') {
      return activeIsPlayer1 ? 'player2' : 'player1';
    }
    return activeIsPlayer1 ? 'player1' : 'player2';
  }

  private findEmptySlots(
    state: GameState,
    side: 'player1' | 'player2'
  ): { row: 'front' | 'back'; column: number }[] {
    const slots: { row: 'front' | 'back'; column: number }[] = [];
    const battleSide = side === 'player1' 
      ? state.battlefield.player1Side 
      : state.battlefield.player2Side;

    // 優先前排
    battleSide.frontLine.forEach((slot, idx) => {
      if (!slot.unit) slots.push({ row: 'front', column: idx + 1 });
    });
    // 再後排
    battleSide.backLine.forEach((slot, idx) => {
      if (!slot.unit) slots.push({ row: 'back', column: idx + 1 });
    });

    return slots;
  }

  private getSlot(state: GameState, position: BattlefieldPosition) {
    const side = position.side === 'player1'
      ? state.battlefield.player1Side
      : state.battlefield.player2Side;
    const line = position.row === 'front' ? side.frontLine : side.backLine;
    return line[position.column - 1];
  }

  private placeOnBattlefield(
    state: GameState,
    card: Card,
    slot: { row: 'front' | 'back'; column: number }
  ): GameState {
    if (!card.position) return state;
    
    const sideKey = card.position.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = slot.row === 'front' ? 'frontLine' : 'backLine';

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    newLine[slot.column - 1] = { ...newLine[slot.column - 1], unit: card };
    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;

    return { ...state, battlefield: newBattlefield };
  }

  private removeFromBattlefield(state: GameState, position: BattlefieldPosition): GameState {
    const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    newLine[position.column - 1] = { ...newLine[position.column - 1], unit: null };
    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;

    return { ...state, battlefield: newBattlefield };
  }

  private removeFromZone(
    state: GameState,
    side: 'player1' | 'player2',
    zone: 'deck' | 'hand' | 'graveyard',
    cardId: string
  ): GameState {
    const playerKey = side;
    const player = state[playerKey];
    const newZone = player[zone].filter(c => c.id !== cardId);

    return {
      ...state,
      [playerKey]: {
        ...player,
        [zone]: newZone,
      },
    };
  }

  private getUnitsOnBattlefield(
    state: GameState,
    side: 'player1' | 'player2',
    filter?: TargetSelector['filter']
  ): { card: Card; position: BattlefieldPosition }[] {
    const units: { card: Card; position: BattlefieldPosition }[] = [];
    const battleSide = side === 'player1'
      ? state.battlefield.player1Side
      : state.battlefield.player2Side;

    const checkLine = (line: typeof battleSide.frontLine, row: 'front' | 'back') => {
      line.forEach((slot, idx) => {
        if (slot.unit) {
          // 應用過濾條件
          if (filter) {
            const def = this.definitions.get(slot.unit.definitionId);
            if (filter.name && def?.name !== filter.name && def?.nameZh !== filter.name) return;
            if (filter.faction && def?.faction !== filter.faction) return;
            if (filter.minAtk !== undefined) {
              const atk = (def?.atk ?? 0) + (slot.unit.atkModifier ?? 0);
              if (atk < filter.minAtk) return;
            }
            if (filter.maxAtk !== undefined) {
              const atk = (def?.atk ?? 0) + (slot.unit.atkModifier ?? 0);
              if (atk > filter.maxAtk) return;
            }
          }
          
          units.push({
            card: slot.unit,
            position: { side, row, column: (idx + 1) as 1 | 2 | 3 | 4 | 5 },
          });
        }
      });
    };

    checkLine(battleSide.frontLine, 'front');
    checkLine(battleSide.backLine, 'back');

    return units;
  }

  private findCardOnBattlefield(state: GameState, cardId: string): { card: Card; position: BattlefieldPosition } | null {
    const sides: Array<{ side: 'player1' | 'player2'; data: typeof state.battlefield.player1Side }> = [
      { side: 'player1', data: state.battlefield.player1Side },
      { side: 'player2', data: state.battlefield.player2Side },
    ];
    for (const { side, data } of sides) {
      for (const [row, line] of [['front', data.frontLine], ['back', data.backLine]] as const) {
        for (let i = 0; i < line.length; i++) {
          const slotUnit = line[i].unit;
          if (slotUnit && slotUnit.id === cardId) {
            return { card: slotUnit, position: { side, row, column: (i + 1) as 1 | 2 | 3 | 4 | 5 } };
          }
        }
      }
    }
    return null;
  }

  private selectRandom<T>(items: T[], count: number): T[] {
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  }

  private modifyUnitAtk(
    state: GameState,
    position: BattlefieldPosition,
    modifier: number
  ): { state: GameState; cardId: string } | null {
    const slot = this.getSlot(state, position);
    if (!slot.unit) return null;

    const newState = this.updateUnitProperty(state, position, {
      atkModifier: (slot.unit.atkModifier ?? 0) + modifier,
    });

    return { state: newState, cardId: slot.unit.id };
  }

  private updateUnitProperty(
    state: GameState,
    position: BattlefieldPosition,
    updates: Partial<Card>
  ): GameState {
    const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
    const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

    const newBattlefield = { ...state.battlefield };
    const newSide = { ...newBattlefield[sideKey] };
    const newLine = [...newSide[lineKey]];
    const slot = newLine[position.column - 1];

    if (slot.unit) {
      newLine[position.column - 1] = {
        ...slot,
        unit: { ...slot.unit, ...updates },
      };
    }

    newSide[lineKey] = newLine;
    newBattlefield[sideKey] = newSide;

    return { ...state, battlefield: newBattlefield };
  }
}

// 工廠函數
export function createActionExecutor(definitions: Map<string, CardDefinition>): ActionExecutor {
  return new ActionExecutor(definitions);
}
